
import { useState } from "react";
import { OptimizedArticleImage } from "./OptimizedArticleImage";

interface NewsItemProps {
  title: string;
  content: string;
  imageUrl?: string;
  createdAt: string;
}

export const NewsItem = ({ 
  title, 
  content, 
  imageUrl, 
  createdAt 
}: NewsItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'nu';
    } else if (diffInHours < 24) {
      return `${diffInHours}h sedan`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) {
        return '1d sedan';
      } else {
        return `${diffInDays}d sedan`;
      }
    }
  };

  const maxLength = 120;
  const shouldTruncate = content.length > maxLength;
  const displayContent = isExpanded || !shouldTruncate 
    ? content 
    : `${content.substring(0, maxLength)}...`;

  return (
    <div className="bg-white px-4 sm:px-8 py-4 sm:py-6 mb-2 mx-auto max-w-5xl">
      {/* Mobile layout: image above text */}
      <div className="flex flex-col sm:hidden">
        <OptimizedArticleImage
          src={imageUrl}
          alt={title}
          aspectRatio="video"
          className="w-full h-40 mb-3"
        />
        
        <div className="flex flex-col px-1">
          <p className="text-[#111418] text-base font-medium leading-snug mb-2">
            {title}
          </p>
          
          <p className="text-[#637588] text-xs font-normal leading-normal mb-2">
            Nyheter · {formatDate(createdAt)}
          </p>
          
          <div>
            <p className="text-[#637588] text-sm font-normal leading-relaxed">
              {displayContent}
            </p>
            
            {shouldTruncate && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-[#111418] text-xs font-medium mt-2 hover:underline"
              >
                {isExpanded ? 'Visa mindre' : 'Läs mer'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop layout: image to the left */}
      <div className="hidden sm:flex gap-6">
        <OptimizedArticleImage
          src={imageUrl}
          alt={title}
          aspectRatio="square"
          className="size-[120px] flex-shrink-0"
        />
        
        <div className="flex flex-1 flex-col justify-center">
          <p className="text-[#111418] text-lg font-medium leading-normal mb-2">
            {title}
          </p>
          
          <p className="text-[#637588] text-sm font-normal leading-normal mb-3">
            Nyheter · {formatDate(createdAt)}
          </p>
          
          <div>
            <p className="text-[#637588] text-base font-normal leading-relaxed">
              {displayContent}
            </p>
            
            {shouldTruncate && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-[#111418] text-sm font-medium mt-2 hover:underline"
              >
                {isExpanded ? 'Visa mindre' : 'Läs mer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
